import { useEffect, useRef } from "react";
import {
  loadDoubleClickPlaceHintShown,
  loadPenShiftHintShown,
  loadToolShortcutHintStages,
  markDoubleClickPlaceHintShown,
  markPenShiftHintShown,
  saveToolShortcutHintStage,
} from "../../../lib/shortcut-hint-preference";
import { useNoticeStore } from "../notice/notice-store";
import { useShowToast, type ShowToast } from "../notice/use-show-toast";
import { useCanvasStore, type CanvasTool } from "../store/canvas-store";

/**
 * 同じツールを続けてこの回数だけ選んだら案内する。左から順に 1 回目・2 回目・3 回目の案内で、
 * 3 回目を出したツールは打ち止めとなり、localStorage を消さないかぎり二度と案内しない。
 */
export const TOOL_SHORTCUT_HINT_THRESHOLDS = [3, 5, 10] as const;

/** ダブルクリック配置の案内を出す連続回数。 */
export const DOUBLE_CLICK_PLACE_HINT_THRESHOLD = 2;

export const PEN_SHIFT_HINT_MESSAGE = "Shift キーを押しながら描くと、まっすぐな線が描けます。";

export const DOUBLE_CLICK_PLACE_HINT_MESSAGE =
  "キャンバスの空白部をダブルクリックすると、前回と同じ種別の図形をすぐに置けます。";

/**
 * ダブルクリック配置で置ける種別に対応する配置ツール。
 * ペンとコメントは last-placed-object-preference の記録対象外で、ダブルクリックしても何も置かれないため除く。
 */
const DOUBLE_CLICK_PLACE_HINT_TOOLS: ReadonlySet<CanvasTool> = new Set<CanvasTool>([
  "place-sticky",
  "place-section",
  "place-shape",
  "place-text",
  "place-table",
  "place-stamp",
]);

/** 段階に対応する閾値を返す。打ち止め（配列の範囲外）なら null。 */
function thresholdForStage(stage: number): number | null {
  return TOOL_SHORTCUT_HINT_THRESHOLDS[stage] ?? null;
}

export interface ToolShortcutHintTarget {
  readonly tool: CanvasTool;
  readonly label: string;
  readonly shortcut: string;
}

export interface ShortcutHintNotices {
  /** ツールボタン押下時に、切り替え前のツールと押されたボタンの情報を渡す。 */
  readonly notifyToolButtonClick: (
    previousTool: CanvasTool,
    target: ToolShortcutHintTarget
  ) => void;
}

export function buildToolShortcutHintMessage(target: ToolShortcutHintTarget): string {
  return `ショートカットキー ${target.shortcut} で「${target.label}」をすぐに選べます。`;
}

/**
 * 同じ本文のお知らせが残っていると notice-store の重複抑止で積めないため、
 * 先に消してから積み直す（2 回目以降の案内を確実に見せ、表示時間も取り直すため）。
 */
function showHint(showToast: ShowToast, message: string): void {
  const { notices, dismiss } = useNoticeStore.getState();
  for (const notice of notices) {
    if (notice.level === "info" && notice.message === message) dismiss(notice.id);
  }
  showToast("info", message);
}

/** 配置ツールのショートカットを段階的に案内し、ペンの Shift 直線操作とダブルクリック配置を一度だけ案内する。 */
export function useShortcutHintNotices(): ShortcutHintNotices {
  const tool = useCanvasStore((state) => state.tool);
  const showToast = useShowToast();
  // localStorage の同期読取りは初回だけ行い、ToolBar の再レンダーでは繰り返さない。
  const stagesRef = useRef<Map<string, number> | null>(null);
  const penShownRef = useRef<boolean | null>(null);
  const doubleClickShownRef = useRef<boolean | null>(null);
  stagesRef.current ??= loadToolShortcutHintStages();
  penShownRef.current ??= loadPenShiftHintShown();
  doubleClickShownRef.current ??= loadDoubleClickPlaceHintShown();
  const stages = stagesRef.current;
  const streakRef = useRef<{ tool: string | null; count: number }>({ tool: null, count: 0 });

  const notifyToolButtonClick = (
    previousTool: CanvasTool,
    target: ToolShortcutHintTarget
  ): void => {
    // 選択中のボタンをもう一度押す操作は解除であり、選択回数には含めない。
    if (previousTool === target.tool) return;

    if (streakRef.current.tool !== target.tool) {
      streakRef.current = { tool: target.tool, count: 0 };
    }
    streakRef.current.count += 1;

    // ダブルクリック配置の案内は段階の進行とは別系統なので、連続回数を戻さずに独立して判定する。
    if (
      DOUBLE_CLICK_PLACE_HINT_TOOLS.has(target.tool) &&
      streakRef.current.count >= DOUBLE_CLICK_PLACE_HINT_THRESHOLD &&
      !doubleClickShownRef.current
    ) {
      // 初回レンダー以降に別タブが案内を出していることがあるため、表示の直前に保存値を読み直す。
      // ここを通るのは 1 ページにつき最多 1 回で（直後に ref を立てるため）、読み直しは積み上がらない。
      const alreadyShown = loadDoubleClickPlaceHintShown();
      // 保存より先にメモリへ反映し、保存失敗時も同じページ内では二重表示しない。
      doubleClickShownRef.current = true;
      if (!alreadyShown) {
        markDoubleClickPlaceHintShown();
        showHint(showToast, DOUBLE_CLICK_PLACE_HINT_MESSAGE);
      }
    }

    const stage = stages.get(target.tool) ?? 0;
    const threshold = thresholdForStage(stage);
    if (threshold === null || streakRef.current.count < threshold) return;

    // 初回レンダー以降に別タブが段階を進めていることがあるため、表示の直前に保存値を読み直す。
    // 読み直すのは閾値へ到達した瞬間だけなので、ボタン押下ごとの読み取りにはならない。
    const savedStage = loadToolShortcutHintStages().get(target.tool) ?? 0;
    if (savedStage > stage) {
      // 別タブが先に案内済み。メモリを合わせ、今回は表示しない。
      // 連続回数は戻さない（案内を出していないため、次の段階の閾値へは今の連続のまま到達してよい）。
      stages.set(target.tool, savedStage);
      return;
    }

    // 保存より先にメモリへ反映し、保存失敗時も同じページ内では二重表示しない。
    stages.set(target.tool, stage + 1);
    saveToolShortcutHintStage(target.tool, stage + 1);
    showHint(showToast, buildToolShortcutHintMessage(target));
    // 次の段階の閾値は、案内の直後から数え直す（案内と案内の間隔を空けるため）。
    streakRef.current.count = 0;
  };

  useEffect(() => {
    // キーボードショートカットなど、ツールバーボタン以外から別ツールへ切り替わった場合も
    // 「続けて選んだ」操作は中断される。配置完了後の idle への自動解除では中断しない。
    if (tool !== "idle" && tool !== streakRef.current.tool) {
      streakRef.current = { tool: null, count: 0 };
    }

    if (tool !== "draw-pen" || penShownRef.current) return;

    // Strict Mode の effect 再実行でも二重表示しないよう、保存より先にフラグを立てる。
    penShownRef.current = true;
    markPenShiftHintShown();
    showToast("info", PEN_SHIFT_HINT_MESSAGE);
  }, [tool, showToast]);

  return { notifyToolButtonClick };
}
